import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAudienceAnalytics } from '../services/analyticsService';
import { socialService, SocialConnection } from '../services/socialService';
import { Users, MapPin, Smile, Meh, Frown, Globe, Facebook, Instagram, Youtube } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import ConnectPlaceholder from './dashboard/ConnectPlaceholder';
import { useSocialConnections } from '../hooks/useSocialConnections';
import AnalyticsLoadingCard from './ui/AnalyticsLoadingCard';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28DFF', '#FF69B4'];
const SENTIMENT_COLORS = {
  positive: '#22c55e',
  neutral: '#eab308',
  negative: '#ef4444',
};

// Helper to render platform icon
const getPlatformIcon = (iconName: string, className: string = "w-4 h-4") => {
  const icons: { [key: string]: React.ReactElement } = {
    'Globe': <Globe className={className} />,
    'Facebook': <Facebook className={className} />,
    'Instagram': <Instagram className={className} />,
    'Youtube': <Youtube className={className} />
  };
  return icons[iconName] || <Globe className={className} />;
};

const CustomPieTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="p-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg">
        <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{`${payload[0].name}: ${payload[0].value}%`}</p>
      </div>
    );
  }
  return null;
};

const CustomBarTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="p-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg">
        <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{`Hour: ${label}`}</p>
        <p className="text-sm text-gray-700 dark:text-gray-300">{`Engagement: ${payload[0].value}`}</p>
      </div>
    );
  }
  return null;
};

const AudienceAnalyticsCard: React.FC = () => {
  const [platformFilter, setPlatformFilter] = React.useState<'all' | 'facebook' | 'instagram' | 'youtube'>('all');
  
  const { connections, isLoading: connectionsLoading, error: connectionsError, handleConnect } = useSocialConnections();

  const { data, isLoading, error } = useQuery({
    queryKey: ['audienceAnalytics', platformFilter],
    queryFn: () => {
      console.log('📊 AudienceAnalyticsCard: Fetching audience data for platform:', platformFilter);
      return fetchAudienceAnalytics('1m', platformFilter === 'all' ? undefined : platformFilter);
    },
    enabled: !!(connections && connections.length > 0), // Only fetch when accounts are connected
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes since it's current data
  });

  // Get available platforms based on connected accounts
  const availablePlatforms = React.useMemo(() => {
    if (!connections || connectionsLoading) return [];
    
    const connectedProviders = connections.map(conn => conn.provider.toLowerCase());
    const platforms = [
      { value: 'all', label: 'All Platforms', icon: '🌐' },
      { value: 'facebook', label: 'Facebook', icon: '📘' },
      { value: 'instagram', label: 'Instagram', icon: '📷' },
      { value: 'youtube', label: 'YouTube', icon: '📺' }
    ];
    
    return [
      platforms[0], // Always include "All"
      ...platforms.slice(1).filter(platform => 
        connectedProviders.includes(platform.value)
      )
    ];
  }, [connections, connectionsLoading]);

  if (isLoading || connectionsLoading) {
    return (
      <AnalyticsLoadingCard 
        title="Audience Analytics"
        message="Analyzing your audience demographics, active hours, and sentiment data..."
        icon="Users"
      />
    );
  }
  if (error) return <div>Error loading audience analytics: {error.message}</div>;
  if (connectionsError) return <div>Error loading connections: {connectionsError.message}</div>;

  if (!connections || connections.length === 0) {
    return (
        <div className="bg-white dark:bg-slate-900/70 rounded-2xl p-8 border border-gray-200/80 dark:border-slate-800 shadow-sm">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Audience Analytics</h2>
            </div>
            <ConnectPlaceholder 
              message="Connect your social media accounts to see your audience analytics."
              onConnectClick={() => handleConnect('facebook')} 
            />
        </div>
    );
  }

  const sentimentData = data ? [
    { name: 'Positive', value: data.sentimentBreakdown.positive },
    { name: 'Neutral', value: data.sentimentBreakdown.neutral },
    { name: 'Negative', value: data.sentimentBreakdown.negative },
  ] : [];

  return (
    <div className="bg-white dark:bg-slate-900/70 rounded-2xl p-8 border border-gray-200/80 dark:border-slate-800 shadow-sm">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div className="flex items-center space-x-3">
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Audience Analytics</h2>
          {data && data.summary && data.summary.total_comments_analyzed > 0 && (
            <div className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
              📊 {data.summary.total_comments_analyzed} Comments Analyzed
            </div>
          )}
        </div>
        
        {/* Platform Filter Dropdown */}
        {availablePlatforms.length > 1 && (
          <div className="relative">
            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value as any)}
              className="px-3 py-2 bg-white dark:bg-slate-800 rounded-lg text-slate-700 dark:text-slate-200 text-sm font-medium border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {availablePlatforms.map((platform) => (
                <option key={platform.value} value={platform.value} className="bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200">
                  {platform.icon} {platform.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      {/* No Data State */}
      {(!data || !data.summary || data.summary.total_comments_analyzed === 0) && (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-400 mb-2">
            No Audience Data Available
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-500 mb-4">
            {platformFilter === 'all' 
              ? 'No audience data found across your connected platforms'
              : `No audience data available for ${platformFilter.charAt(0).toUpperCase() + platformFilter.slice(1)}`
            }
          </p>
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              💡 Audience analytics are generated from comments and interactions on your posts. 
              {platformFilter === 'youtube' && ' YouTube data may be temporarily unavailable due to API limits.'}
            </p>
          </div>
        </div>
      )}

      {/* Data Available State */}
      {data && data.summary && data.summary.total_comments_analyzed > 0 && (
        <div className="space-y-8">
          
          {/* Only show demographics for specific platforms, not "All Platforms" */}
          {platformFilter !== 'all' && (
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                {platformFilter.charAt(0).toUpperCase() + platformFilter.slice(1)} Demographics
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center space-x-3 bg-gray-100 dark:bg-slate-800/50 p-4 rounded-lg">
                  <Users className="h-6 w-6 text-indigo-600 dark:text-indigo-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Age</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                      {data?.demographics?.age || 'Not available'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 bg-gray-100 dark:bg-slate-800/50 p-4 rounded-lg">
                  <Users className="h-6 w-6 text-emerald-600 dark:text-emerald-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Gender</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                      {data?.demographics?.gender || 'Not available'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 bg-gray-100 dark:bg-slate-800/50 p-4 rounded-lg">
                  <MapPin className="h-6 w-6 text-sky-600 dark:text-sky-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Location</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                      {data?.demographics?.location || 'Not available'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Show a helpful message when "All Platforms" is selected */}
          {platformFilter === 'all' && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <div>
                  <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    Combined Platform View
                  </h4>
                  <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                    Select a specific platform to view detailed demographics. Combined demographics across platforms can be misleading due to different user bases.
                  </p>
                </div>
              </div>
            </div>
          )}

        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Active Hours (Engagement)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data?.activeHours} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(128, 128, 128, 0.2)" />
              <XAxis dataKey="hour" tick={{ fill: '#6b7280', fontSize: 12 }} axisLine={{ stroke: 'rgba(128, 128, 128, 0.2)' }} tickLine={{ stroke: 'rgba(128, 128, 128, 0.2)' }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} axisLine={{ stroke: 'rgba(128, 128, 128, 0.2)' }} tickLine={{ stroke: 'rgba(128, 128, 128, 0.2)' }} />
              <Tooltip content={<CustomBarTooltip />} cursor={{ fill: 'rgba(128, 128, 128, 0.1)' }} />
              <Bar dataKey="engagement" fill="#f97316" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Audience by Country</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data?.audienceDensityByCountry}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={100}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                  nameKey="country"
                >
                  {data?.audienceDensityByCountry?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomPieTooltip />} />
                <Legend iconType="circle" iconSize={8} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Sentiment Breakdown</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={sentimentData}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={100}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                  nameKey="name"
                >
                  {sentimentData.map((entry, index) => (
                    <Cell key={`cell-sentiment-${index}`} fill={SENTIMENT_COLORS[entry.name.toLowerCase() as keyof typeof SENTIMENT_COLORS]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomPieTooltip />} />
                <Legend iconType="circle" iconSize={8} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        </div>
      )}

    </div>
  );
};

export default AudienceAnalyticsCard;
